import { Component, OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  NgxIntlTelInputModule,
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { AssessmentService } from '../../../../core/services/assessment/assessment.service';
import {
  Nationality,
  Occupation,
  Country,
  State,
  City,
  MedicationCategory,
  Medication,
  AntibioticCategory,
  Debridement,
  VacuumAssistedClosureModel,
  VacuumOptionDto,
  GetListSurgeryModel,
} from '../../../../core/models/patient/patient.model';
import {
  CalculateAssessmentsScoresDto,
  CalculateAssessmentsScoresRequest,
  GlassAssessmentMetadata,
  GlassSelectedVesselSummary,
  QuestionVM,
  QuestionGroup,
} from '../../../../core/models/assessment/assessment.model';

import {
  AllergyService,
  AllergyCategory,
} from '../../../../core/services/allergy/allergy.service';
import { OrganizationService } from '../../../../core/services/organization/organization.service';
import {
  Organization,
  Facility,
} from '../../../../core/models/organization/organization.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { PermissionService } from '../../../../core/services/user/permission.service';
import { WoundService } from 'app/core/services/wound/wound.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastService } from 'app/core/services/toast.service';
import { GetListWoundCareProductModel } from 'app/core/models/wound-care-product/wound-care-product.model';
import { WoundCareProductService } from 'app/core/services/wound-care-product/wound-care-product.service';
import { BaseApiResponse } from 'app/core/models/api/api.model';
import { ChronicConditionService } from 'app/core/services/chronic-condition/chronic-condition.service';
import { ChronicCondition } from 'app/core/models/chronic-condition/chronic-condition.model';
import { delay, map, Observable, of, take, tap } from 'rxjs';
import { ImmunizationStatus } from 'app/core/models/immunization-status/immunization-status.model';
import { FamilyMedicalHistory } from 'app/core/models/family-medical-history/family-medical-history.model';
import { FamilyMedicalHistoryService } from 'app/core/services/family-medical-history/family-medical-history.service';
import { ImmunizationStatusService } from 'app/core/services/immunization-status/immunization-status.service';
import { UlcerWoundType } from 'app/core/models/ulcer-wound-type/ulcer-wound-type.model';
import { UlcerWoundTypeService } from 'app/core/services/ulcer-wound-type/ulcer-wound-type.service';
import { getCountryCodeFromDialCode } from '../../../../utils/country-code-utils';
import { Symptom } from 'app/core/models/symptom/symptom.model';
import { SymptomService } from 'app/core/services/symptom/symptom.service';
import { ModalService } from 'app/main/shared/services/modal.service';
import { AskToSpecialistModalComponent } from 'app/shared/components/modal/ask-to-specialist-modal/ask-to-specialist-modal.component';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { PatientPersonalInfoStepComponent } from './patient-personal-info-step/patient-personal-info-step.component';
import { PatientMedicalInfoStepComponent } from './patient-medical-info-step/patient-medical-info-step.component';
import { DecimalOnlyDirective } from 'app/shared/directives/decimal-only.directive';
import { EmailValidatorService } from 'app/main/validators/email.validator';
import { GlassScoreSelectorComponent } from 'app/main/components/glass-score-selector/glass-score-selector.component';
import {
  GlassTreatmentRecommendationKey,
  getGlassStage,
  getGlassTreatmentRecommendation,
} from 'app/core/constants/glass-scoring';

function noFutureDateValidator(
  control: AbstractControl,
): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const inputDate = new Date(control.value);
  inputDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (inputDate > today) {
    return { futureDate: true };
  }

  return null;
}

function noOldDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const inputDate = new Date(control.value);
  const minDate = new Date('1900-01-01');

  if (inputDate < minDate) {
    return { oldDate: true };
  }

  return null;
}

interface QuestionDependencyRule {
  triggerQuestionId: string;
  triggerAnswerValue: string;
  targetQuestionId: string;
}

const QUESTION_DEPENDENCY_RULES: QuestionDependencyRule[] = [
  {
    triggerQuestionId: '2130cd8e-9474-46f6-9f93-ccfc293818f5',
    triggerAnswerValue: 'LT_5',
    targetQuestionId: 'llsls232345-d423-4bc5-86c9-a5bac81b709e',
  },
  {
    triggerQuestionId: '213ded8e-9474-46f6-9f93-ccfc293818f5',
    triggerAnswerValue: '0',
    targetQuestionId: 'b81d1fv0-d423-4bc5-86c9-a5bag81b759e',
  },
  {
    triggerQuestionId: 'fofo0202-9474-46f6-9f93-202002osos',
    triggerAnswerValue: '0',
    targetQuestionId: 'b8ee1450-d423-4bc5-86c9-a5bac81b709e',
  },
];

@Component({
  selector: 'app-patient-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgxIntlTelInputModule,
    NgbAccordionModule,
    TranslateModule,
    NgSelectModule,
    PatientPersonalInfoStepComponent,
    PatientMedicalInfoStepComponent,
    SelectComponent,
    DecimalOnlyDirective,
    GlassScoreSelectorComponent,
  ],
  templateUrl: './patient-create.component.html',
  styleUrl: './patient-create.component.scss',
})
export class PatientCreateComponent implements OnInit, AfterViewInit {
  patientForm: FormGroup;
  currentStep = 1;
  nationalities: Nationality[] = [];
  occupations: Occupation[] = [];
  countries: Country[] = [];
  cities: State[] = [];
  districts: City[] = [];
  readonly WEIGHT_LOSS_QUESTION_ID = '2130cd8e-9474-46f6-9f93-ccfc293818f5';
  readonly DIABETIC_ULCER_WOUND_TYPE_ID =
    '9c5f6992-8cc5-4856-a2fd-3a67b484b036';
  allergyCategories: AllergyCategory[] = [];
  selectedAllergies: string[] = [];
  medications: MedicationCategory[] = [];
  selectedMedications: string[] = [];
  surgeries: GetListSurgeryModel[] = [];
  selectedSurgeries: string[] = [];
  organizations: Organization[] = [];
  facilities: Facility[] = [];
  antibiotics: AntibioticCategory[] = [];
  selectedAntibiotics: string[] = [];
  debridements: Debridement[] = [];
  selectedDebridements: string[] = [];
  vacList: VacuumAssistedClosureModel[] = [];
  selectedVacType: number | null = null;
  selectedVacSubOptions: { [optionId: string]: string } = {};
  vacTouched = false;

  currentUser: UserSessionModel | null = null;

  woundCareProducts: GetListWoundCareProductModel[] = [];
  selectedWoundCareProducts: string[] = [];

  questionGroups: QuestionGroup[] = [];
  allQuestionGroups: QuestionGroup[] = [];
  assessmentScores: CalculateAssessmentsScoresDto[] = [];
  groupScores: Map<string, CalculateAssessmentsScoresDto> = new Map();
  glassSelections: Map<string, GlassSelectedVesselSummary[]> = new Map();
  woundQuestions: QuestionVM[] = [];
  selectedWoundType: string | null = null; // Track selected wound type name
  selectedWoundTypeId: string | null = null; // Track selected wound type ID
  isLoadingWoundQuestions = false;
  woundQuestionsError: string | null = null;
  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;
  patientSaved = false;
  savedPatientRecordId: string | null = null;
  savedPatientId: string | null = null;
  savedWoundId: string | null = null;
  originalPid: string | null = null;

  results: ChronicCondition[] = [];
  showDropdown = false;
  isLoadingMore = false;
  currentPageIndex = 0;
  pageSize = 10;
  totalCount = 0;
  hasNext = false;

  isWoundExistenceOpen = true;
  isWoundDetailsOpen = false;
  medicalHistoryOpen = false;

  accordionState = {
    chronicConditions: false,
    surgeries: false,
    allergies: false,
    familyHistory: false,
    immunizations: false,
    medications: false,
    woundCareProducts: false,
    antibiotics: false,
    debridement: false,
    vac: false,
  };

  immunizationStatus: ImmunizationStatus[] = [];
  familyMedicalHistory: FamilyMedicalHistory[] = [];

  selectedFamilyHistory: string[] = [];
  selectedImmunizations: string[] = [];

  todayDate: string = new Date().toISOString().split('T')[0];

  chronicConditionArray: ChronicCondition[] = [];

  // Edit Mode
  isEditMode = false;
  patientId: string | null = null;
  isLoadingPatient = false;

  hasWound: boolean = false;
  hasWoundFromQueryParam: boolean = false;
  woundConsultationId: string = '';
  private readonly consultationActionCompletedStoragePrefix =
    'sore:woundConsultationActionCompleted:';
  isAskDisabled = false;

  genders: Array<{ id: number; name: string }> = [];
  maritalStatuses: Array<{ id: number; name: string }> = [];
  livingConditionsOptions: Array<{ id: number; name: string }> = [];
  ulcerWoundTypeOptions: UlcerWoundType[] = [];
  symptomOptions: Symptom[] = [];
  dischargeTypeOptions: Array<{ id: number; name: string }> = [];
  healingStatusOptions: Array<{ id: number; name: string }> = [];
  priorManagementOptions: Array<{ id: number; name: string }> = [];
  skinTypeOptions: Array<{
    id: number;
    name: string;
    color: string;
    textColor: string;
  }> = [];
  dietTypeOptions: Array<{ id: number; name: string }> = [];
  bloodTypeOptions: Array<{ id: number; name: string }> = [];
  alcoholConsumptionOptions: Array<{ id: number; name: string }> = [];
  smokingStatusOptions: Array<{ id: number; name: string }> = [];
  
  defaultPatientCountry: CountryISO = CountryISO.Turkey;
  defaultEmergencyContactCountry: CountryISO = CountryISO.Turkey;
  
  get isSystemAdmin(): boolean {
    const userOrgId = this.currentUser?.userOrganizations?.[0]?.organizationId;
    return !userOrgId;
  }

  get isDischargeSelected(): boolean {
    const symptomValue = this.patientForm?.get('woundDetails.symptoms')?.value;
    if (!symptomValue) return false;
    if (Array.isArray(symptomValue)) {
      return symptomValue.some(
        (s) => String(s) === '20cfcc31-f578-45ca-962f-f0cf384e515f',
      );
    }
    return String(symptomValue) === '20cfcc31-f578-45ca-962f-f0cf384e515f';
  }

  isStepperHidden = false;
  initialStep: number | null = null;
  parentWoundId: string | null = null;
  woundTypeParam: number | null = null;

  get painSliderPercentage(): number {
    const value = this.patientForm?.get('woundDetails.painLevel')?.value || 0;
    return (value / 10) * 100;
  }

  get isCalculateButtonDisabled(): boolean {
    const hasWound = this.patientForm.get('woundDetails.hasWound')?.value;
    const woundDetailsInvalid = this.patientForm.get('woundDetails')?.invalid;

    const hasAssessmentAnswers = this.questionGroups.some((group) =>
      group.questions.some((q) => q.answerOptionId),
    );

    if (hasWound === true) {
      return woundDetailsInvalid === true || !hasAssessmentAnswers;
    } else if (hasWound === false) {
      return !hasAssessmentAnswers;
    }

    return true;
  }

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private patientsService: PatientsService,
    private allergyService: AllergyService,
    private organizationService: OrganizationService,
    private currentUserService: CurrentUserService,
    private assessmentService: AssessmentService,
    private woundService: WoundService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private woundCareProductService: WoundCareProductService,
    private chronicConditionService: ChronicConditionService,
    private familyMedialHistoryService: FamilyMedicalHistoryService,
    private immunizationService: ImmunizationStatusService,
    private ulcerWoundTypeService: UlcerWoundTypeService,
    private symptomService: SymptomService,
    private modalService: ModalService,
    private permissionService: PermissionService,
    private emailValidatorService: EmailValidatorService,
  ) {}

  onCheckPatientExistence(): void {
    const pidControl = this.patientForm.get('personalInfo.pid');
    const orgIdControl = this.patientForm.get('personalInfo.organizationId');

    if (pidControl?.valid && orgIdControl?.value) {
      this.patientsService
        .checkPatientExistence(pidControl.value, orgIdControl.value)
        .subscribe({
          next: (res) => {
            if (
              this.isEditMode &&
              this.originalPid &&
              String(pidControl.value) === String(this.originalPid)
            ) {
              if (pidControl.errors) {
                delete pidControl.errors['patientExists'];
                if (Object.keys(pidControl.errors).length === 0) {
                  pidControl.setErrors(null);
                }
              }
              return;
            }

            if (res.data?.exists) {
              pidControl.setErrors({
                ...pidControl.errors,
                patientExists: true,
              });
            } else {
              if (pidControl.errors) {
                delete pidControl.errors['patientExists'];
                if (Object.keys(pidControl.errors).length === 0) {
                  pidControl.setErrors(null);
                }
              }
            }
          },
        });
    }
  }

  ngOnInit(): void {
    this.initSelectOptions();

    this.patientForm = this.fb.group({
      searchTerm: ['', []],
      personalInfo: this.fb.group({
        firstName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern('^[a-zA-ZğüşöçİĞÜŞÖÇıI\\s]+$'),
          ],
        ],
        lastName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50),
            Validators.pattern('^[a-zA-ZğüşöçİĞÜŞÖÇıI\\s]+$'),
          ],
        ],
        pid: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(30),
            Validators.pattern('^[a-zA-Z0-9-]+$'),
          ],
        ],
        dob: [
          '',
          [Validators.required, noFutureDateValidator, noOldDateValidator],
        ],
        gender: [null, Validators.required],
        phoneNumber: ['', Validators.required],
        email: ['', [Validators.required, this.emailValidatorService.validEmailFormat()]],
        occupation: [null, [Validators.required, Validators.maxLength(100)]],
        nationality: [null, Validators.required],
        maritalStatus: [null],
        organizationId: [null, Validators.required],
        facilityId: [{ value: null, disabled: true }, Validators.required],
      }),
      addressInfo: this.fb.group({
        country: [null, Validators.required],
        city: [{ value: null, disabled: true }, Validators.required],
        district: [{ value: null, disabled: true }, Validators.required],
        neighborhood: [
          '',
          [
            Validators.maxLength(100),
            Validators.pattern('^[a-zA-ZğüşöçİĞÜŞÖÇıI\\s]+$'),
          ],
        ],
        street: ['', Validators.maxLength(100)],
        buildingNo: [
          '',
          [Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]+$')],
        ],
        apartmentNo: [
          '',
          [Validators.maxLength(10), Validators.pattern('^[0-9]+$')],
        ],
      }),
      emergencyContact: this.fb.group({
        firstName: ['', [Validators.minLength(2), Validators.maxLength(50)]],
        lastName: ['', [Validators.minLength(2), Validators.maxLength(50)]],
        phoneNumber: [''],
        email: ['', this.emailValidatorService.validEmailFormat()],
        relationship: [null],
      }),
      // Physical & Lifestyle
      physicalLifestyle: this.fb.group({
        height: [
          null,
          [
            Validators.min(50),
            Validators.max(250),
            Validators.pattern('^[0-9]+([.,][0-9]{1,2})?$'),
          ],
        ],
        weight: [
          null,
          [
            Validators.min(20),
            Validators.max(300),
            Validators.pattern('^[0-9]+([.,][0-9]{1,2})?$'),
          ],
        ],
        waistCircumference: [
          null,
          [
            Validators.min(30),
            Validators.max(200),
            Validators.pattern('^[0-9]+([.,][0-9]{1,2})?$'),
          ],
        ],
        skinType: [null],
        bloodType: [null],
        livingConditions: [null],
        dietType: [null],
        physicalActivityLevel: [null],
        smokingStatus: [null],
        smokingDuration: [null, Validators.pattern('^[0-9]+$')],
        smokingAmount: [null, Validators.pattern('^[0-9]+$')],
        alcoholConsumption: [null],
        alcoholPortionPerDay: [null, Validators.pattern('^[0-9]+$')],
      }),
      // Step 2: Medical Information
      medicalHistory: this.fb.group({
        searchTerm: [''],
        chronicConditions: [''],
        surgeries: [''],
        allergies: [''],
        familyHistory: [''],
        immunizations: [''],
        medications: [''],
        woundCareProducts: [''],
      }),
      habits: this.fb.group({
        smoking: this.fb.group({ status: [''], duration: [''], amount: [''] }),
        alcohol: this.fb.group({ status: [''], duration: [''], amount: [''] }),
        other: this.fb.group({ status: [''], duration: [''], amount: [''] }),
      }),
      clinicalInfo: this.fb.group({
        ambulatory: [false],
        icu: [false],
        hospital: [false],
        acutelyIll: [false],
        edema: [false],
        unplannedWeightLoss: [false],
        weightLossAmount: [''],
        hasWound: [null],
        askToSpecialist: [null],
      }),
    });

    this.currentUser = this.currentUserService.GetSession();
    const userOrgId = this.currentUser?.userOrganizations?.[0]?.organizationId;

    if (userOrgId) {
      this.patientForm.get('personalInfo.organizationId')?.setValue(userOrgId);
      this.patientForm.get('personalInfo.facilityId')?.clearValidators();
      this.patientForm.get('personalInfo.facilityId')?.updateValueAndValidity();
    }

    this.setupSearchTermListener();
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.patientId = params['id'];

        this.route.queryParams
          .pipe(take(1))
          .subscribe((queryParams) => {
            // Extract all query parameters synchronously before any async operations
            if (queryParams['hideStepper'] === 'true') {
              this.isStepperHidden = true;
            }
            
            if (queryParams['startStep']) {
              const step = Number(queryParams['startStep']);
              if (!isNaN(step)) {
                this.initialStep = step;
                this.goToStep(this.initialStep);
              }
            }
            
            if (queryParams['parentWoundId']) {
              this.parentWoundId = queryParams['parentWoundId'];
            }
            
            if (queryParams['woundType']) {
              const wt = Number(queryParams['woundType']);
              if (!isNaN(wt)) {
                this.woundTypeParam = wt;
              }
            }

            // Load data based on stepper state
            if (!(this.isStepperHidden && this.initialStep === 3)) {
              this.loadStep1Data();
              if (!userOrgId) {
                this.loadOrganizations();
              }
            }
            
            // Handle wound-related query parameters after state is set
            if (queryParams['hasWound']) {
              const hasWoundParam = queryParams['hasWound'] === 'true';
              this.hasWound = hasWoundParam;
              this.hasWoundFromQueryParam = true;

              const hasWoundControl = this.patientForm.get(
                'clinicalInfo.hasWound',
              );
              const askToSpecialistControl = this.patientForm.get(
                'clinicalInfo.askToSpecialist',
              );

              if (hasWoundControl) {
                hasWoundControl.patchValue(hasWoundParam, { emitEvent: false });

                hasWoundControl.markAsTouched();
                hasWoundControl.updateValueAndValidity();

                this.isLoadingWoundQuestions = true;
                setTimeout(() => {
                  this.onWoundStatusChange();
                }, 0);
              }

              if (askToSpecialistControl) {
                askToSpecialistControl.disable();
              }
            }
          });

        this.loadPatientData(this.patientId);
      } else {
        this.loadStep1Data();
        if (!userOrgId) {
          this.loadOrganizations();
        }
      }
    });

    this.onOrganizationChange();
    this.onCountryChange();
    this.onCityChange();
  }

  loadStep1Data(): void {
    this.loadNationalities();
    this.loadOccupations();
    this.loadCountries();
  }

  loadPatientData(patientId: string): void {
    this.isLoadingPatient = true;
    this.patientsService.getPatientById(patientId).subscribe({
      next: async (response) => {
        if (response) {
          this.populateFormWithPatientData(response);

          if (!(this.isStepperHidden && this.initialStep === 3)) {
            await this.loadStep2Data();
          }
        } else {
          console.error('Failed to load patient data or data is null');
          this.toastService.showToast(
            this.translate.instant('errorLoadingPatient'),
            'error',
          );
        }
        this.isLoadingPatient = false;
      },
      error: (err) => {
        console.error('Error loading patient:', err);
        this.toastService.showToast(
          this.translate.instant('errorLoadingPatient'),
          'error',
        );
        this.isLoadingPatient = false;
      },
    });
  }

  populateFormWithPatientData(patient: any): void {
    this.savedPatientRecordId = patient.recordId || null;
    this.originalPid = String(patient.bsnNumber || '');

    const personalInfo = this.patientForm.get('personalInfo');
    if (personalInfo) {
      // Get organization and facility from patientOrganization
      // Check multiple paths for organizationId due to API inconsistencies
      const orgId =
        patient.patientOrganization?.organizationId ||
        patient.patientOrganization?.organizationtId ||
        patient.organizationId ||
        null;
      console.log('Extracted Organization ID:', orgId);

      // Ensure the organization is in the list so it can be selected
      if (orgId && patient.patientOrganization?.organizationName) {
        const alreadyInList = this.organizations.some((o) => o.id === orgId);
        if (!alreadyInList) {
          this.organizations = [
            ...this.organizations,
            {
              id: orgId,
              name: patient.patientOrganization.organizationName,
            } as any,
          ];
        }
      }

      const facilityId =
        patient.patientOrganization?.patientFacilities?.[0]?.facilityId || null;

      // Get occupation ID from occupationDto array
      const occupationId = patient.occupationDto?.[0]?.occupationId || null;

      personalInfo.patchValue({
        firstName: patient.name || '',
        lastName: patient.surname || '',
        pid: patient.bsnNumber || '',
        dob: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
        gender: patient.gender,
        phoneNumber: patient.phoneNumber || '',
        email: patient.email || '',
        occupation: occupationId,
        nationality: patient.nationalityId || null,
        maritalStatus: patient.maritalStatus,
        organizationId: orgId,
        facilityId: facilityId,
      });

      if (orgId) {
        this.patientForm.get('personalInfo.facilityId')?.enable();

        const patientFacilities =
          patient.patientOrganization?.patientFacilities;
        if (patientFacilities && patientFacilities.length > 0) {
          this.facilities = patientFacilities.map((f: any) => ({
            id: f.facilityId,
            name: f.facilityName,
          }));
          if (facilityId) {
            this.patientForm
              .get('personalInfo.facilityId')
              ?.setValue(facilityId);
          }
        } else if (
          this.initialStep !== 3 &&
          this.permissionService.hasPermission('system_admin_claim')
        ) {
          this.organizationService.getOrganizationById(orgId).subscribe({
            next: (response) => {
              if (response.data?.facilities) {
                this.facilities = response.data.facilities;
                if (facilityId) {
                  this.patientForm
                    .get('personalInfo.facilityId')
                    ?.setValue(facilityId);
                }
              }
            },
          });
        }
      }
    }

    const addressInfo = this.patientForm.get('addressInfo');
    if (addressInfo) {
      console.log('Patient address info:', patient);

      addressInfo.patchValue({
        country: patient.countryId || null,
        neighborhood: patient.neighborhood || '',
        street: patient.street || '',
        buildingNo: patient.buildingNo || '',
        apartmentNo: patient.apartmentNo || '',
      });

      if (
        patient.countryId &&
        !(this.isStepperHidden && this.initialStep === 3)
      ) {
        this.patientsService.getListState(patient.countryId).subscribe({
          next: (data) => {
            this.cities = data;
            console.log('Cities loaded:', this.cities);

            if (patient.cityId) {
              const cityId = Number(patient.cityId);
              console.log('Setting city to:', cityId);

              setTimeout(() => {
                addressInfo.get('city')?.setValue(cityId);
                console.log(
                  'City value after set:',
                  addressInfo.get('city')?.value,
                );
              }, 0);

              this.patientsService.getListCity(cityId).subscribe({
                next: (districtData) => {
                  this.districts = districtData;
                  console.log('Districts loaded:', this.districts);
                  if (patient.districtId) {
                    const districtId = Number(patient.districtId);
                    console.log('Setting district to:', districtId);

                    setTimeout(() => {
                      addressInfo.get('district')?.setValue(districtId);
                      console.log(
                        'District value after set:',
                        addressInfo.get('district')?.value,
                      );
                    }, 0);
                  }
                },
              });
            }
          },
        });
      }
    }

    const emergencyContact = this.patientForm.get('emergencyContact');
    if (emergencyContact && patient.emergencyContact) {
      emergencyContact.patchValue({
        firstName: patient.emergencyContact.name || '',
        lastName: patient.emergencyContact.surname || '',
        phoneNumber: patient.emergencyContact.phoneNumber || '',
        email: patient.emergencyContact.email || '',
        relationship: patient.emergencyContact.emergencyRelationship,
      });
      
      // Extract country code from emergency contact phone number
      if (patient.emergencyContact.countryCode) {
        const emergencyCountryCode = getCountryCodeFromDialCode(patient.emergencyContact.countryCode);
        if (emergencyCountryCode) {
          this.defaultEmergencyContactCountry = emergencyCountryCode as CountryISO;
        }
      }
    }
    
    // Extract country code from patient phone number
    if (patient.countryCode) {
      const patientCountryCode = getCountryCodeFromDialCode(patient.countryCode);
      if (patientCountryCode) {
        this.defaultPatientCountry = patientCountryCode as CountryISO;
      }
    }

    const physicalLifestyle = this.patientForm.get('physicalLifestyle');
    if (physicalLifestyle) {
      physicalLifestyle.patchValue({
        height: patient.height,
        weight: patient.weight,
        waistCircumference: patient.waistCircumference,
        skinType: patient.skinType,
        bloodType: patient.bloodType,
        livingConditions: patient.livingConditions,
        dietType: patient.dietType,
        physicalActivityLevel: patient.physicalActivityLevel,
        smokingStatus: patient.smokingInformation?.smokingStatus,
        smokingDuration: patient.smokingInformation?.durationInYears,
        smokingAmount: patient.smokingInformation?.amountPerDay,
        alcoholConsumption: patient.alcoholConsumption?.type,
        alcoholPortionPerDay: patient.alcoholConsumption?.portionPerDay,
      });
    }

    if (patient.allergyDto && patient.allergyDto.length > 0) {
      this.selectedAllergies = patient.allergyDto.map((a: any) => a.allergyId);
    }
    if (patient.medicationDto && patient.medicationDto.length > 0) {
      this.selectedMedications = patient.medicationDto.map(
        (m: any) => m.medicationId,
      );
    }
    if (patient.surgeryDto && patient.surgeryDto.length > 0) {
      this.selectedSurgeries = patient.surgeryDto.map((s: any) => s.surgeryId);
    }
    if (patient.chronicConditionDto && patient.chronicConditionDto.length > 0) {
      this.chronicConditionArray = patient.chronicConditionDto.map(
        (c: any) =>
          ({
            id: c.chronicConditionId,
            name: c.name,
          }) as ChronicCondition,
      );
    }
    if (
      patient.familyMedicalHistoryDto &&
      patient.familyMedicalHistoryDto.length > 0
    ) {
      this.selectedFamilyHistory = patient.familyMedicalHistoryDto.map(
        (f: any) => f.familyMedicalHistoryId,
      );
    }
    if (
      patient.immunizationStatusDto &&
      patient.immunizationStatusDto.length > 0
    ) {
      this.selectedImmunizations = patient.immunizationStatusDto.map(
        (i: any) => i.immunizationStatusId,
      );
    }
  }

  async loadStep2Data(): Promise<void> {
    if (this.allergyCategories.length === 0) this.loadAllergies();
    if (this.medications.length === 0) this.loadMedications();
    if (this.surgeries.length === 0) this.loadSurgeries();
    if (this.immunizationStatus.length === 0)
      await this.getListImmunizationStatus();
    if (this.familyMedicalHistory.length === 0)
      await this.getListFamilyMedicalHistory();

    this.livingConditionsOptions = [
      {
        id: 0,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.livingConditions.none',
        ),
      },
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.livingConditions.alone',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.livingConditions.withFamily',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.livingConditions.assistedLiving',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.livingConditions.nursingHome',
        ),
      },
    ];
  }

  initializeWoundDetailsForm(): void {
    if (!this.patientForm.get('woundDetails')) {
      this.patientForm.addControl(
        'woundDetails',
        this.fb.group({
          hasWound: [null, Validators.required],
          ulcerWoundType: [null],
          symptoms: [null],
          dischargeType: [null],
          onset: [''],
          healingStatus: [null],
          painLevel: [0],
          priorManagement: [null],
          askToSpecialist: [null],
          previousWeight: [null, [Validators.min(0)]],
          currentWeight: [null, [Validators.min(0)]],
        }),
      );

      if (this.patientSaved) {
        const patientWeight = this.patientForm.get(
          'physicalLifestyle.weight',
        )?.value;
        if (patientWeight) {
          this.patientForm
            .get('woundDetails.currentWeight')
            ?.setValue(patientWeight);
        }
      }
      this.patientForm
        .get('woundDetails.hasWound')
        ?.valueChanges.subscribe((hasWound) => {
          const woundDetails = this.patientForm.get('woundDetails');

          if (hasWound === true) {
            woundDetails
              ?.get('ulcerWoundType')
              ?.setValidators([Validators.required]);
            woundDetails?.get('symptoms')?.setValidators([Validators.required]);
            woundDetails
              ?.get('healingStatus')
              ?.setValidators([Validators.required]);
            woundDetails
              ?.get('priorManagement')
              ?.setValidators([Validators.required]);
            woundDetails?.get('onset')?.setValidators([Validators.required]);
            woundDetails
              ?.get('painLevel')
              ?.setValidators([Validators.required]);
          } else {
            woundDetails?.get('ulcerWoundType')?.clearValidators();
            woundDetails?.get('symptoms')?.clearValidators();
            woundDetails?.get('healingStatus')?.clearValidators();
            woundDetails?.get('priorManagement')?.clearValidators();
            woundDetails?.get('onset')?.clearValidators();
            woundDetails?.get('painLevel')?.clearValidators();
          }

          woundDetails?.get('ulcerWoundType')?.updateValueAndValidity();
          woundDetails?.get('symptoms')?.updateValueAndValidity();
          woundDetails?.get('healingStatus')?.updateValueAndValidity();
          woundDetails?.get('priorManagement')?.updateValueAndValidity();
          woundDetails?.get('onset')?.updateValueAndValidity();
          woundDetails?.get('painLevel')?.updateValueAndValidity();
        });

      this.patientForm
        .get('woundDetails.ulcerWoundType')
        ?.valueChanges.subscribe((woundTypeId) => {
          if (woundTypeId) {
            const selectedType = this.ulcerWoundTypeOptions.find(
              (wt) => wt.id === woundTypeId,
            );
            if (selectedType) {
              this.selectedWoundTypeId = woundTypeId;
              this.selectedWoundType = selectedType.name;
              this.resetTemplateAnswers();
              this.applyWoundTypeFilter();
            }
          } else {
            // Some select implementations briefly emit `null` while swapping
            // between options. Clear only if the control is still empty next tick.
            setTimeout(() => {
              const currentValue = this.patientForm.get(
                'woundDetails.ulcerWoundType',
              )?.value;

              if (currentValue) {
                return;
              }

              this.selectedWoundTypeId = null;
              this.selectedWoundType = null;
              this.questionGroups = [];
              this.groupScores.clear();
            });
          }
        });
    }

    this.route.queryParams.pipe(delay(0), take(1)).subscribe((queryParams) => {
      if (queryParams['hasWound']) {
        const hasWoundParam = queryParams['hasWound'] === 'true';
        this.hasWound = hasWoundParam;
        this.hasWoundFromQueryParam = true;
        
        if (queryParams['woundConsultationId']) {
          this.isAskDisabled = true;
          this.woundConsultationId = queryParams['woundConsultationId'];
        }
        
        const hasWoundControl = this.patientForm.get('woundDetails.hasWound');

        if (hasWoundControl) {
          hasWoundControl.patchValue(hasWoundParam, { emitEvent: false });

          hasWoundControl.markAsTouched();
          hasWoundControl.updateValueAndValidity();

          setTimeout(() => {
            this.onWoundStatusChange();
          }, 0);
        }
      }
    });
  }

  goToStep(step: number): void {
    if (step === 3) {
      this.initializeWoundDetailsForm();
      if (this.woundCareProducts.length === 0) this.loadWoundCareProducts();
      if (this.antibiotics.length === 0) this.loadAntibiotics();
      if (this.debridements.length === 0) this.loadDebridements();
      if (this.vacList.length === 0) this.loadVacuumAssistedClosure();
      if (this.ulcerWoundTypeOptions.length === 0) this.getListUlcerWoundType();
      if (this.symptomOptions.length === 0) this.getListSymptom();
    }

    this.currentStep = step;
  }

  loadNationalities(): void {
    this.patientsService.getListNationality().subscribe({
      next: (data) => {
        this.nationalities = data;
        console.log('Nationalities loaded:', this.nationalities);
      },
      error: (err) => {
        console.error('Error loading nationalities:', err);
        this.nationalities = [];
      },
    });
  }

  loadOccupations(): void {
    this.patientsService.getListOccupation().subscribe({
      next: (data) => {
        this.occupations = data;
        console.log('Occupations loaded:', this.occupations);
      },
      error: (err) => {
        console.error('Error loading occupations:', err);
        this.occupations = [];
      },
    });
  }

  loadCountries(): void {
    this.patientsService.getListCountry().subscribe({
      next: (data) => {
        this.countries = data;
        console.log('Countries loaded:', this.countries);
        console.log('Countries count:', this.countries.length);
      },
      error: (err) => {
        console.error('Error loading countries:', err);
        this.countries = [];
      },
    });
  }

  loadAllergies(): void {
    this.allergyService.getListAllergy().subscribe({
      next: (data) => {
        this.allergyCategories = data;
        console.log('Allergy categories loaded:', this.allergyCategories);
      },
      error: (err) => {
        console.error('Error loading allergy categories:', err);
        this.allergyCategories = [];
      },
    });
  }

  toggleAllergy(allergyId: string): void {
    const index = this.selectedAllergies.indexOf(allergyId);
    if (index > -1) {
      this.selectedAllergies.splice(index, 1);
    } else {
      this.selectedAllergies.push(allergyId);
    }
  }

  isAllergySelected(allergyId: string): boolean {
    return this.selectedAllergies.includes(allergyId);
  }

  loadMedications(): void {
    this.patientsService.getListMedication().subscribe({
      next: (data) => {
        this.medications = data;
        console.log('Medications loaded:', this.medications);
      },
      error: (err) => {
        console.error('Error loading medications:', err);
        this.medications = [];
      },
    });
  }

  toggleMedication(medicationId: string): void {
    const index = this.selectedMedications.indexOf(medicationId);
    if (index > -1) {
      this.selectedMedications.splice(index, 1);
    } else {
      this.selectedMedications.push(medicationId);
    }
  }

  isMedicationSelected(medicationId: string): boolean {
    return this.selectedMedications.includes(medicationId);
  }

  loadSurgeries(): void {
    this.patientsService.getListSurgery().subscribe({
      next: (data) => {
        this.surgeries = data;
        console.log('Surgeries loaded:', this.surgeries);
      },
      error: (err) => {
        console.error('Error loading surgeries:', err);
        this.surgeries = [];
      },
    });
  }

  loadWoundCareProducts(): void {
    this.woundCareProductService.getWoundCareProducts().subscribe({
      next: (res: BaseApiResponse<GetListWoundCareProductModel[]>) => {
        this.woundCareProducts = res.data;
        console.log('Wound care products loaded:', this.woundCareProducts);
      },
      error: (err) => {
        console.error('Error loading wound care products:', err);
        this.woundCareProducts = [];
      },
    });
  }

  toggleWoundCareProduct(productId: string): void {
    const index = this.selectedWoundCareProducts.indexOf(productId);
    if (index > -1) {
      this.selectedWoundCareProducts.splice(index, 1);
    } else {
      this.selectedWoundCareProducts.push(productId);
    }
  }

  isWoundCareProductSelected(productId: string): boolean {
    return this.selectedWoundCareProducts.includes(productId);
  }

  toggleSurgery(surgeryId: string): void {
    const index = this.selectedSurgeries.indexOf(surgeryId);
    if (index > -1) {
      this.selectedSurgeries.splice(index, 1);
    } else {
      this.selectedSurgeries.push(surgeryId);
    }
  }

  isSurgerySelected(surgeryId: string): boolean {
    return this.selectedSurgeries.includes(surgeryId);
  }

  loadAntibiotics(): void {
    this.patientsService.getListAntibiotic().subscribe({
      next: (data) => {
        this.antibiotics = data;
        console.log('Antibiotics loaded:', this.antibiotics);
      },
      error: (err) => {
        console.error('Error loading antibiotics:', err);
        this.antibiotics = [];
      },
    });
  }

  toggleAntibiotic(antibioticId: string): void {
    const index = this.selectedAntibiotics.indexOf(antibioticId);
    if (index > -1) {
      this.selectedAntibiotics.splice(index, 1);
    } else {
      this.selectedAntibiotics.push(antibioticId);
    }
  }

  isAntibioticSelected(antibioticId: string): boolean {
    return this.selectedAntibiotics.includes(antibioticId);
  }

  loadDebridements(): void {
    this.patientsService.getListDebridement().subscribe({
      next: (data) => {
        this.debridements = data;
        console.log('Debridements loaded:', this.debridements);
      },
      error: (err) => {
        console.error('Error loading debridements:', err);
        this.debridements = [];
      },
    });
  }

  toggleDebridement(debridementId: string): void {
    const index = this.selectedDebridements.indexOf(debridementId);
    if (index > -1) {
      this.selectedDebridements.splice(index, 1);
    } else {
      this.selectedDebridements.push(debridementId);
    }
  }

  isDebridementSelected(debridementId: string): boolean {
    return this.selectedDebridements.includes(debridementId);
  }

  loadVacuumAssistedClosure(): void {
    this.patientsService.getListVacuumAssistedClosure().subscribe({
      next: (data) => {
        this.vacList = data;
        console.log('VAC list loaded:', this.vacList);
      },
      error: (err) => {
        console.error('Error loading VAC list:', err);
        this.vacList = [];
      },
    });
  }

  onVacTypeChange(vacId: string, type: number): void {
    const isSelected = this.selectedVacType === type;

    if (isSelected) {
      // Toggle off
      this.selectedVacType = null;
      this.selectedVacSubOptions = {};
    } else {
      // Select new
      this.selectedVacType = type;
      this.selectedVacSubOptions = {};
    }

    // Reset the touched flag when user changes selection
    this.vacTouched = false;
  }

  onVacSubOptionChange(optionId: string, answerId: string): void {
    if (this.selectedVacSubOptions[optionId] === answerId) {
      delete this.selectedVacSubOptions[optionId];
    } else {
      this.selectedVacSubOptions[optionId] = answerId;
    }
  }

  isVacSubOptionSelected(optionId: string, answerId: string): boolean {
    return this.selectedVacSubOptions[optionId] === answerId;
  }

  getSpongeOptions(): VacuumOptionDto[] {
    const spongeType = this.vacList.find((v) => v.vacuumType === 1);
    return spongeType ? spongeType.vacuumOptionDtos : [];
  }

  isVacValid(): boolean {
    if (this.selectedVacType === null || this.selectedVacType === 0) {
      return true;
    }
    if (this.selectedVacType === 1) {
      const options = this.getSpongeOptions();
      return options.every((opt) => !!this.selectedVacSubOptions[opt.id]);
    }
    return true;
  }

  loadOrganizations(): void {
    this.organizationService.getAllOrganizationItems().subscribe({
      next: (data) => {
        const currentList = this.organizations;
        this.organizations = data;
        const selectedId = this.patientForm.get(
          'personalInfo.organizationId',
        )?.value;
        if (selectedId) {
          const foundInNew = this.organizations.find(
            (o) => o.id === selectedId,
          );
          if (!foundInNew) {
            const foundInOld = currentList.find((o) => o.id === selectedId);
            if (foundInOld) {
              this.organizations = [...this.organizations, foundInOld];
            }
          }
        }
        console.log('Organizations loaded:', this.organizations);
      },
      error: (err) => {
        console.error('Error loading organizations:', err);
        this.organizations = [];
      },
    });
  }

  onOrganizationChange(): void {
    this.patientForm
      .get('personalInfo.organizationId')
      ?.valueChanges.subscribe((value) => {
        const orgId = this.patientForm.get(
          'personalInfo.organizationId',
        )?.value;
        this.facilities = [];
        const facilityControl = this.patientForm.get('personalInfo.facilityId');
        facilityControl?.setValue(null);

        if (
          orgId &&
          this.initialStep !== 3 &&
          this.permissionService.hasPermission('system_admin_claim')
        ) {
          facilityControl?.enable();
          this.organizationService.getOrganizationById(orgId).subscribe({
            next: (response) => {
              if (response.data && response.data.facilities) {
                this.facilities = response.data.facilities;
                console.log('Facilities loaded:', this.facilities);
              }
            },
            error: (err) => {
              console.error('Error loading facilities:', err);
              facilityControl?.disable();
            },
          });
        } else {
          facilityControl?.disable();
        }

        const pidControl = this.patientForm.get('personalInfo.pid');
        if (pidControl?.errors?.['patientExists']) {
          delete pidControl.errors['patientExists'];
          if (Object.keys(pidControl.errors).length === 0) {
            pidControl.setErrors(null);
          }
          pidControl.updateValueAndValidity({ emitEvent: false });
        }

        this.onCheckPatientExistence();
      });
  }

  onCountryChange(): void {
    this.patientForm
      .get('addressInfo.country')
      ?.valueChanges.subscribe((value) => {
        const cityControl = this.patientForm.get('addressInfo.city');
        const districtControl = this.patientForm.get('addressInfo.district');

        const countryId = this.patientForm.get('addressInfo.country')?.value;

        // Clear validators FIRST to prevent validation errors when setting to null
        cityControl?.clearValidators();
        districtControl?.clearValidators();

        // Then set values to null
        cityControl?.setValue(null);
        districtControl?.setValue(null);

        // Update validity immediately
        cityControl?.updateValueAndValidity();
        districtControl?.updateValueAndValidity();

        this.cities = [];
        this.districts = [];

        if (!countryId) {
          cityControl?.disable();
          return;
        }

        cityControl?.enable();

        this.patientsService.getListState(countryId).subscribe({
          next: (data) => {
            this.cities = data;
            console.log('Cities loaded:', this.cities);

            const cityControl = this.patientForm.get('addressInfo.city');
            const districtControl = this.patientForm.get(
              'addressInfo.district',
            );

            if (this.cities.length === 0) {
              cityControl?.clearValidators();
              districtControl?.clearValidators();
            } else {
              cityControl?.setValidators([Validators.required]);
              districtControl?.setValidators([Validators.required]);
            }

            cityControl?.updateValueAndValidity();
            districtControl?.updateValueAndValidity();
          },
          error: (err) => {
            console.error('Error loading cities:', err);
            this.cities = [];
            const cityControl = this.patientForm.get('addressInfo.city');
            const districtControl = this.patientForm.get(
              'addressInfo.district',
            );
            cityControl?.clearValidators();
            districtControl?.clearValidators();
            cityControl?.updateValueAndValidity();
            districtControl?.updateValueAndValidity();
          },
        });
      });
  }

  onCityChange(): void {
    this.patientForm
      .get('addressInfo.city')
      ?.valueChanges.subscribe((cityId) => {
        const districtControl = this.patientForm.get('addressInfo.district');

        this.districts = [];
        districtControl?.setValue(null);

        if (!cityId) {
          districtControl?.disable();
          return;
        }

        districtControl?.enable();

        this.patientsService.getListCity(cityId).subscribe({
          next: (data) => {
            this.districts = data;

            if (this.districts.length === 0) {
              districtControl?.clearValidators();
            } else {
              districtControl?.setValidators([Validators.required]);
            }

            districtControl?.updateValueAndValidity();
          },
          error: () => {
            this.districts = [];
            districtControl?.disable();
            districtControl?.clearValidators();
            districtControl?.updateValueAndValidity();
          },
        });
      });
  }

  async onSubmit() {
    if (this.patientForm.valid) {
      if (!this.isVacValid()) {
        this.vacTouched = true;
        console.warn('VAC options required for Sponge type');
        return;
      }
      console.log('Form Valid, proceeding to next step...');
      this.currentStep++;
      if (this.currentStep === 2) {
        await this.loadStep2Data();
      }
      console.log('Current Step:', this.currentStep);
      console.log('Form Data:', this.patientForm.value);
    } else {
      console.log('Form is invalid');
      this.patientForm.markAllAsTouched();
      if (!this.isVacValid()) {
        this.vacTouched = true;
      }
    }
  }

  transformFormToApi(formData: any): any {
    const personalInfo = formData.personalInfo || {};
    const addressInfo = formData.addressInfo || {};
    const emergencyContact = formData.emergencyContact || {};
    const physicalLifestyle = formData.physicalLifestyle || {};
    const phoneObj = personalInfo.phoneNumber;
    let phoneNumber = null;
    let countryCode = null;

    if (phoneObj && typeof phoneObj === 'object') {
      const rawNumber = phoneObj.nationalNumber?.replace(/\D/g, '') || null;
      phoneNumber = rawNumber ? String(parseInt(rawNumber, 10)) : null;
      countryCode = phoneObj.dialCode || null;
    } else if (typeof phoneObj === 'string') {
      const rawNumber = phoneObj.replace(/\D/g, '') || null;
      phoneNumber = rawNumber ? String(parseInt(rawNumber, 10)) : null;
    }

    let dateOfBirth = null;
    if (personalInfo.dob) {
      const dobDate = new Date(personalInfo.dob);
      dateOfBirth = dobDate.toISOString();
    }

    let organizationName: string | null = null;
    let facilities: { facilityId: string; facilityName: string }[] = [];

    const userOrg = this.currentUser?.userOrganizations?.[0];

    if (userOrg && !this.isSystemAdmin) {
      organizationName = userOrg.organizationName || null;
      if (userOrg.userFacilities && userOrg.userFacilities.length > 0) {
        facilities = userOrg.userFacilities.map((f: any) => ({
          facilityId: f.facilityId,
          facilityName: f.facilityName,
        }));
      }
    } else {
      const selectedOrg = this.organizations.find(
        (o) => o.id === personalInfo.organizationId,
      );
      organizationName = selectedOrg ? selectedOrg.name : null;

      if (personalInfo.facilityId) {
        const selectedFacility = this.facilities.find(
          (f) => f.id === personalInfo.facilityId,
        );
        if (selectedFacility) {
          facilities.push({
            facilityId: selectedFacility.id,
            facilityName: selectedFacility.name,
          });
        }
      }
    }

    return {
      name: personalInfo.firstName || '',
      surname: personalInfo.lastName || '',
      bsnNumber: personalInfo.pid || '',
      dateOfBirth: dateOfBirth,
      gender: personalInfo.gender || 1,
      countryCode: countryCode,
      phoneNumber: phoneNumber,
      email: personalInfo.email || null,
      placeOfResidence: null,
      height: physicalLifestyle.height ?? null,
      weight: physicalLifestyle.weight ?? null,
      waistCircumference: physicalLifestyle.waistCircumference ?? null,
      nationalityId: personalInfo.nationality || null,
      countryId: addressInfo.country || null,
      cityId: addressInfo.city || null,
      districtId: addressInfo.district || null,
      neighborhood: addressInfo.neighborhood || '',
      street: addressInfo.street || '',
      buildingNo: addressInfo.buildingNo || '',
      apartmentNo: addressInfo.apartmentNo || '',
      maritalStatus: personalInfo.maritalStatus || 1,
      livingConditions: physicalLifestyle.livingConditions || 1,
      physicalActivityLevel: physicalLifestyle.physicalActivityLevel || 1,
      dietType: physicalLifestyle.dietType || 1,
      skinType: physicalLifestyle.skinType || 1,
      bloodType: physicalLifestyle.bloodType || 1,
      emergencyContact: {
        name: emergencyContact.firstName || '',
        surname: emergencyContact.lastName || '',
        countryCode: (() => {
          const ecPhone = emergencyContact.phoneNumber;
          if (ecPhone && typeof ecPhone === 'object') {
            return ecPhone.dialCode || '';
          }
          return '';
        })(),
        phoneNumber: (() => {
          const ecPhone = emergencyContact.phoneNumber;
          if (ecPhone && typeof ecPhone === 'object') {
            const raw = ecPhone.nationalNumber?.replace(/\D/g, '') || '';
            return raw ? String(parseInt(raw, 10)) : '';
          } else if (typeof ecPhone === 'string') {
            const raw = ecPhone.replace(/\D/g, '') || '';
            return raw ? String(parseInt(raw, 10)) : '';
          }
          return '';
        })(),
        email: emergencyContact.email || '',
        emergencyRelationship: emergencyContact.relationship || 1,
      },
      alcoholConsumption: {
        type: physicalLifestyle.alcoholConsumption ?? 1,
        portionPerDay: physicalLifestyle.alcoholPortionPerDay ?? null,
      },
      smokingInformation: {
        smokingStatus: physicalLifestyle.smokingStatus ?? 1,
        durationInYears: physicalLifestyle.smokingDuration ?? null,
        amountPerDay: physicalLifestyle.smokingAmount ?? null,
        quittedDate: null,
        packYear:
          physicalLifestyle.smokingAmount && physicalLifestyle.smokingDuration
            ? (physicalLifestyle.smokingAmount / 20) *
              physicalLifestyle.smokingDuration
            : null,
      },
      occupation: {
        id: personalInfo.occupation || '',
        otherProfession: null,
      },
      occupationName: null,
      surgeryIds: this.selectedSurgeries,
      allergyIds: this.selectedAllergies,
      chronicConditionIds: this.chronicConditionArray.map((cc) => cc.id),
      medicationIds: this.selectedMedications,
      organizationId: personalInfo.organizationId || null,
      organizationName: organizationName,
      facilities: facilities,
      woundCareProductIds: this.selectedWoundCareProducts,
      antibioticIds: this.selectedAntibiotics,
      debridementIds: this.selectedDebridements,
      createVacuumAssistedClosureDto: this.buildVacDto(),
      familyMedicalHistoryIds: this.selectedFamilyHistory,
      immunizationStatusIds: this.selectedImmunizations,
    };
  }

  buildVacDto(): any {
    if (this.selectedVacType === null) return null;

    const answers: any[] = [];

    if (this.selectedVacType === 1) {
      Object.entries(this.selectedVacSubOptions).forEach(
        ([optionId, answerId]) => {
          if (optionId && answerId) {
            answers.push({
              subOptionId: optionId,
              answerId: answerId,
            });
          }
        },
      );
    }

    return {
      vacuumType: this.selectedVacType,
      closureAnswer: answers,
    };
  }

  savePatient() {
    if (this.patientForm.valid) {
      if (!this.isVacValid()) {
        this.vacTouched = true;
        console.warn('VAC options required for Sponge type');
        return;
      }
      console.log('Saving patient data...');
      console.log('Form Data:', this.patientForm.value);
      const apiData = this.transformFormToApi(this.patientForm.value);
      if (this.isEditMode && this.patientId) {
        apiData.id = this.patientId;
      }

      console.log('Transformed API Data:', apiData);

      const saveObservable = this.isEditMode
        ? this.patientsService.updatePatient(apiData)
        : this.patientsService.createPatient(apiData);

      saveObservable.subscribe({
        next: (response) => {
          if (response.isSuccess) {
            if (this.isEditMode) {
              console.log('Patient updated successfully:', response);
              this.toastService.showToast(
                this.translate.instant('successUpdatePatientDesc'),
                'success',
              );
              this.router.navigate(['/patient/list']);
            } else {
              console.log('Patient created successfully:', response);
              this.patientSaved = true;
              this.savedPatientRecordId = response.data?.recordId || null;
              this.savedPatientId = response.data?.id || null;

              this.patientId = response.data?.id || undefined;
              this.toastService.showToast(
                this.translate.instant('patientAddedSuccessfully'),
                'success',
              );
              this.router.navigateByUrl('/patient/list');
            }
          } else {
            console.error('Patient save failed:', response.fail);
            const errorKey = this.isEditMode
              ? 'errorUpdatePatientDesc'
              : 'errorCreatePatientDesc';
            this.toastService.showToast(
              this.translate.instant(errorKey),
              'error',
            );
          }
        },
        error: (err) => {
          console.error('Error saving patient:', err);
          const errorKey = this.isEditMode
            ? 'errorUpdatePatientDesc'
            : 'errorCreatePatientDesc';
          this.toastService.showToast(
            this.translate.instant(errorKey),
            'error',
          );
        },
      });
    } else {
      console.log('Form is invalid');
      this.patientForm.markAllAsTouched();
      if (!this.isVacValid()) {
        this.vacTouched = true;
      }
    }
  }

  fetchWoundQuestions(woundType: number): void {
    this.isLoadingWoundQuestions = true;
    this.woundQuestionsError = null;

    this.assessmentService.getWoundQuestions(woundType).subscribe({
        next: (res) => {
          if (!res.isSuccess || !res.data?.items) {
            this.woundQuestionsError =
              'Failed to fetch wound assessment questions.';
            this.isLoadingWoundQuestions = false;
            return;
          }

          const items = res.data.items;

          this.woundQuestions = items
            .map((item) => {
              const q = item.assessmentQuestion;

              const options = (q.assessmentQuestionScores || [])
                .map((s) => ({
                  id: s.assessmentAnswerOption.id,
                  text: s.assessmentAnswerOption.text,
                  value: s.assessmentAnswerOption.value,
                  score: s.score,
                  displayOrder: s.assessmentAnswerOption.displayOrder,
                  tooltip: s.assessmentAnswerOption.tooltip,
                }))
                .sort((a, b) => a.displayOrder - b.displayOrder);

              return {
                id: q.id,
                text: q.questionDefinition.text,
                shortCode: q.questionDefinition.shortCode,
                templateId: q.assessmentTemplate?.id,
                templateName: q.assessmentTemplate?.name,
                templateCode: q.assessmentTemplate?.code,
                applicableUlcerWoundTypeIds:
                  q.assessmentTemplate?.applicableUlcerWoundTypeIds || [],
                isRequired: q.isRequired,
                displayOrder: q.displayOrder,
                inputType: q.questionDefinition.inputType,
                options,
                answerOptionId: undefined,
              } as QuestionVM;
            })
            .sort((a, b) => a.displayOrder - b.displayOrder);

          const groups = new Map<string, QuestionGroup>();

          this.woundQuestions.forEach((q) => {
            const key = q.templateCode || 'Other';
            if (!groups.has(key)) {
              groups.set(key, {
                title: q.templateName || q.templateCode || 'Other',
                code: q.templateCode,
                questions: [],
                isOpen: false,
              });
            }
            groups.get(key)?.questions.push(q);
          });

          this.allQuestionGroups = Array.from(groups.values());
          this.questionGroups = [...this.allQuestionGroups];
          if (woundType === 0) {
            this.sortNoWoundQuestionGroups();
          } else {
            this.applyWoundTypeFilter();
          }
          if (this.questionGroups.length > 0) {
            // Medical history is the first accordion to open, not question groups
            this.medicalHistoryOpen = true;
            this.questionGroups[0].isOpen = false;
          }
          this.evaluateQuestionDependencies();
          this.isLoadingWoundQuestions = false;
        },
        error: (err) => {
          console.error(err);
          this.woundQuestionsError =
            'An error occurred while fetching wound assessment questions.';
          this.isLoadingWoundQuestions = false;
        },
      });
  }

  onWoundStatusChange(): void {
    const hasWound = this.patientForm.get('woundDetails.hasWound')?.value;
    if (hasWound === true) {
      this.isWoundExistenceOpen = false;
      this.isWoundDetailsOpen = true;
      this.patientForm.get('woundDetails.ulcerWoundType')?.reset();
      this.selectedWoundType = null;
      this.selectedWoundTypeId = null;
      this.clearWoundQuestionState();
      this.fetchWoundQuestions(1);
    } else if (hasWound === false) {
      this.isWoundExistenceOpen = false;
      this.isWoundDetailsOpen = false;
      this.selectedWoundType = null;
      this.selectedWoundTypeId = null;
      this.fetchWoundQuestions(0);

      const patientWeight = this.patientForm.get(
        'physicalLifestyle.weight',
      )?.value;
      if (patientWeight) {
        this.patientForm
          .get('woundDetails.currentWeight')
          ?.setValue(patientWeight);
      }
    } else if (hasWound === 'ask') {
      this.isWoundExistenceOpen = true;
      this.isWoundDetailsOpen = false;
      this.selectedWoundType = null;
      this.selectedWoundTypeId = null;
      this.clearWoundQuestionState();
      const modalRef = this.modalService.openComponentModal(
        AskToSpecialistModalComponent,
        {
          size: 'lg',
          backdrop: true,
          keyboard: true,
          data: {
            patientId: this.patientId ?? '',
            patientName: this.patientForm.get('personalInfo').get('firstName')
              .value,
            patientSurname: this.patientForm.get('personalInfo').get('lastName')
              .value,
          },
        },
      );

      modalRef.componentInstance.patientId = this.patientId ?? '';
      ((modalRef.componentInstance.patientName = this.patientForm
        .get('personalInfo')
        .get('firstName').value),
        (modalRef.componentInstance.patientSurname = this.patientForm
          .get('personalInfo')
          .get('lastName').value),
        modalRef.closed.subscribe((result) => {
          if (result === 'success') {
            // this.userUpdated.emit();
          }
        }));
    }
  }

  nextToQuestions() {
    this.isWoundDetailsOpen = false;
    if (this.questionGroups.length > 0) {
      this.questionGroups[0].isOpen = true;
    }
  }

  private sortNoWoundQuestionGroups(): void {
    // Keep the legacy no-wound ordering where Fontaine/Rutherford precedes VCSS.
    this.questionGroups.sort((a, b) => {
      const aCode = (a.code || '').toLowerCase();
      const bCode = (b.code || '').toLowerCase();
      const aIsVcss = aCode.includes('vcss');
      const bIsVcss = bCode.includes('vcss');
      const aIsFontaine = aCode.includes('fontaine');
      const bIsFontaine = bCode.includes('fontaine');

      if (aIsVcss && bIsFontaine) return 1;
      if (aIsFontaine && bIsVcss) return -1;
      return 0;
    });
  }

  private applyWoundTypeFilter(): void {
    if (!this.selectedWoundTypeId) {
      this.questionGroups = [];
      return;
    }

    this.questionGroups = this.allQuestionGroups.filter((group) =>
      group.questions.some((question) =>
        question.applicableUlcerWoundTypeIds.includes(this.selectedWoundTypeId!),
      ),
    );

    this.sortSelectedWoundQuestionGroups();
    this.updateWifiGroupAvailability();
    this.updateGlassGroupAvailability();
  }

  private sortSelectedWoundQuestionGroups(): void {
    if (!this.isDiabeticUlcerSelected()) {
      return;
    }

    const sortWeight = (group: QuestionGroup): number => {
      const code = (group.code || '').toLowerCase();

      if (code.includes('sinbad')) return 1;
      if (code.includes('maggitwagner')) return 2;
      if (code.includes('iwgdf')) return 3;
      if (code.includes('wifi')) return 4;
      if (code.includes('glass')) return 5;

      return 99;
    };

    this.questionGroups.sort((a, b) => sortWeight(a) - sortWeight(b));
  }

  private clearWoundQuestionState(): void {
    this.woundQuestions = [];
    this.questionGroups = [];
    this.allQuestionGroups = [];
    this.groupScores.clear();
    this.isLoadingWoundQuestions = false;
    this.woundQuestionsError = null;
  }

  private resetTemplateAnswers(): void {
    this.woundQuestions.forEach((q) => {
      q.answerOptionId = undefined;
    });
    this.questionGroups.forEach((group) => {
      group.questions.forEach((q) => {
        q.answerOptionId = undefined;
      });
    });
    this.groupScores.clear();
  }

  onQuestionOptionClick(q: QuestionVM, optionId: string, event?: Event): void {
    if (q.isDisabled) {
      return;
    }
    
    const isToggling = q.answerOptionId === optionId;
    
    if (isToggling) {
      q.answerOptionId = undefined;
      this.evaluateQuestionDependencies();
      this.recalculateWifiGroupScoreIfPossible();
      this.recalculateGlassGroupScoreIfPossible();
      
      const currentGroup = this.questionGroups.find((group) =>
        group.questions.some((question) => question.id === q.id),
      );
      if (currentGroup) {
        const templateId = currentGroup.questions[0]?.templateId;
        
        if (templateId && this.groupScores.has(templateId)) {
          this.calculateGroupScore(currentGroup);
          
          const hasAnyAnswers = currentGroup.questions.some(
            (question) => question.answerOptionId,
          );
          
          if (!hasAnyAnswers) {
            currentGroup.isOpen = false;
          }
        }
      }
    } else {
      q.answerOptionId = optionId;
      this.evaluateQuestionDependencies();
      this.recalculateWifiGroupScoreIfPossible();
      this.recalculateGlassGroupScoreIfPossible();
      this.isWoundExistenceOpen = false;
      this.isWoundDetailsOpen = false;
      this.medicalHistoryOpen = false;
      this.checkAndAdvanceAccordion(q);
    }
  }

  onGlassOptionSelected(event: { question: QuestionVM; optionId: string }): void {
    this.onQuestionOptionClick(event.question, event.optionId);
  }

  onGlassSelectionChanged(
    templateId: string | undefined,
    selections: GlassSelectedVesselSummary[],
  ): void {
    if (!templateId) {
      return;
    }

    this.glassSelections.set(templateId, selections);
  }

  private checkAndAdvanceAccordion(q: QuestionVM): void {
    const currentGroup = this.questionGroups.find((group) =>
      group.questions.some((question) => question.id === q.id),
    );

    if (!currentGroup || !q.answerOptionId) return;

    const allAnswered = currentGroup.questions.every(
      (question) => question.answerOptionId || question.isDisabled,
    );

    if (allAnswered) {
      // Calculate the score for this group
      this.calculateGroupScore(currentGroup);

      const currentIndex = this.questionGroups.indexOf(currentGroup);

      if (currentIndex > -1 && currentIndex < this.questionGroups.length - 1) {
        currentGroup.isOpen = false;
        this.questionGroups[currentIndex + 1].isOpen = true;
      } else if (currentIndex === this.questionGroups.length - 1) {
        currentGroup.isOpen = false;
      }
    }
  }

  getGroupScore(templateId: string): CalculateAssessmentsScoresDto | undefined {
    return this.groupScores.get(templateId);
  }

  isIWGDFGroup(group: QuestionGroup): boolean {
    return (group.code || '').toLowerCase().includes('iwgdf');
  }

  isWifiGroup(group: QuestionGroup): boolean {
    return (group.code || '').toLowerCase().includes('wifi');
  }

  isGlassGroup(group: QuestionGroup): boolean {
    return (group.code || '').toLowerCase().includes('glass');
  }

  getDisplayGroupTitle(group: QuestionGroup): string {
    return this.formatWifiDisplayText(group.title);
  }

  getDisplayQuestionText(text: string | undefined): string {
    return this.formatWifiDisplayText(text);
  }

  getDisplayOptionText(text: string | undefined): string {
    return this.formatWifiDisplayText(text, true);
  }

  getGlassGroupDisabledMessage(group: QuestionGroup): string | null {
    const disabledQuestion = group.questions.find((question) => question.isDisabled);
    return disabledQuestion?.disabledReason || null;
  }

  getIWGDFFollowUpText(
    score: CalculateAssessmentsScoresDto | undefined,
  ): string {
    if (!score) {
      return '';
    }

    const translationKey = this.getIWGDFFollowUpTranslationKey(
      score.totalScore,
    );
    return translationKey ? this.translate.instant(translationKey) : '';
  }

  private calculateGroupScore(group: QuestionGroup): void {
    const templateId = group.questions[0]?.templateId;
    if (!templateId) {
      return;
    }

    if (this.isWifiGroup(group)) {
      this.calculateWifiGroupScore(group);
      return;
    }

    if (this.isGlassGroup(group)) {
      this.calculateGlassGroupScore(group);
      return;
    }

    const answers = group.questions
      .filter((q) => q.answerOptionId && !q.isDisabled)
      .map((q) => ({
        assessmentQuestionId: q.id,
        assessmentAnswerOptionId: q.answerOptionId!,
      }));

    const requiredCount = group.questions.filter((q) => !q.isDisabled).length;
    if (answers.length === 0 || (this.isIWGDFGroup(group) && answers.length < requiredCount)) {
      this.groupScores.delete(templateId);
      return;
    }

    const requestPayload = {
      calculateAssessmentsScores: [
        {
          assessmentTemplateId: templateId,
          answers: answers,
        },
      ],
      patientId: this.patientId || this.savedPatientId,
    };

    this.woundService.calculateAssessmentsScores(requestPayload).subscribe({
      next: (response) => {
        if (
          response.isSuccess &&
          response.data?.calculateAssessmentsScoresDtos
        ) {
          const scoreDto = response.data.calculateAssessmentsScoresDtos[0];
          if (scoreDto) {
            this.groupScores.set(templateId, scoreDto);
            this.updateWifiGroupAvailability();
            this.recalculateWifiGroupScoreIfPossible();
            this.updateGlassGroupAvailability();
            this.recalculateGlassGroupScoreIfPossible();
          }
        }
      },
      error: (error) => {
        console.error('Error calculating assessment scores:', error);
      },
    });
  }

  evaluateQuestionDependencies(): void {
    QUESTION_DEPENDENCY_RULES.forEach((rule) => {
      const targetQuestion = this.woundQuestions.find(
        (q) => q.id === rule.targetQuestionId,
      );
      if (targetQuestion) {
        targetQuestion.isDisabled = true;
        targetQuestion.disabledReason = this.translate.instant(
          'assessment.disabledReason',
        );
      }
    });

    QUESTION_DEPENDENCY_RULES.forEach((rule) => {
      const triggerQuestion = this.woundQuestions.find(
        (q) => q.id === rule.triggerQuestionId,
      );
      if (!triggerQuestion || !triggerQuestion.answerOptionId) {
        return;
      }

      const selectedOption = triggerQuestion.options.find(
        (opt) => opt.id === triggerQuestion.answerOptionId,
      );

      if (selectedOption && selectedOption.value !== rule.triggerAnswerValue) {
        const targetQuestion = this.woundQuestions.find(
          (q) => q.id === rule.targetQuestionId,
        );
        if (targetQuestion) {
          targetQuestion.isDisabled = false;
          targetQuestion.disabledReason = undefined;
        }
      } else {
        const targetQuestion = this.woundQuestions.find(
          (q) => q.id === rule.targetQuestionId,
        );
        if (targetQuestion) {
          targetQuestion.isDisabled = true;
          targetQuestion.answerOptionId = undefined;
        }
      }
    });

    this.updateWifiGroupAvailability();
    this.updateGlassGroupAvailability();
  }

  private isDiabeticUlcerSelected(): boolean {
    return this.selectedWoundTypeId === this.DIABETIC_ULCER_WOUND_TYPE_ID;
  }

  private findGroupByCode(codeFragment: string): QuestionGroup | undefined {
    return this.questionGroups.find((group) =>
      (group.code || '').toLowerCase().includes(codeFragment),
    );
  }

  private findScoreByCode(codeFragment: string): CalculateAssessmentsScoresDto | undefined {
    const group = this.findGroupByCode(codeFragment);
    const templateId = group?.questions[0]?.templateId;

    return templateId ? this.groupScores.get(templateId) : undefined;
  }

  private updateWifiGroupAvailability(): void {
    const wifiGroup = this.findGroupByCode('wifi');
    if (!wifiGroup) {
      return;
    }

    if (!this.isDiabeticUlcerSelected()) {
      wifiGroup.questions.forEach((question) => {
        question.isDisabled = false;
        question.disabledReason = undefined;
      });
      return;
    }

    const hasDependencies =
      !!this.findScoreByCode('iwgdf') && !!this.findScoreByCode('maggitwagner');

    wifiGroup.questions.forEach((question) => {
      question.isDisabled = !hasDependencies;
      question.disabledReason = hasDependencies
        ? undefined
        : this.translate.instant(
            'patientCreate.woundAssessment.wifi.validation.dependencies',
          );

      if (!hasDependencies) {
        question.answerOptionId = undefined;
      }
    });

    if (!hasDependencies) {
      const templateId = wifiGroup.questions[0]?.templateId;
      if (templateId) {
        this.groupScores.delete(templateId);
      }
    }

    this.updateGlassGroupAvailability();
  }

  private calculateWifiGroupScore(group: QuestionGroup): void {
    const templateId = group.questions[0]?.templateId;
    if (!templateId) {
      return;
    }

    const breakdown = this.getWifiBreakdown(group);
    if (!breakdown) {
      this.groupScores.delete(templateId);
      return;
    }

    const stage = this.getWifiStage(
      breakdown.wound,
      breakdown.ischemia,
      breakdown.footInfection,
    );

    this.groupScores.set(templateId, {
      assessmentTemplateId: templateId,
      assessmentTemplateCode: group.code || 'Wifi',
      totalScore: stage,
      riskCategoryName: this.translate.instant(
        this.getWifiRiskLevelTranslationKey(stage),
      ),
      description: this.getWifiMajorAmputationRate(stage),
    });
  }

  private recalculateWifiGroupScoreIfPossible(): void {
    const wifiGroup = this.findGroupByCode('wifi');
    if (!wifiGroup) {
      this.recalculateGlassGroupScoreIfPossible();
      return;
    }

    const hasAnswer = wifiGroup.questions.some((question) => question.answerOptionId);
    if (!hasAnswer) {
      const templateId = wifiGroup.questions[0]?.templateId;
      if (templateId) {
        this.groupScores.delete(templateId);
      }
      this.updateGlassGroupAvailability();
      this.recalculateGlassGroupScoreIfPossible();
      return;
    }

    this.calculateWifiGroupScore(wifiGroup);
    this.updateGlassGroupAvailability();
    this.recalculateGlassGroupScoreIfPossible();
  }

  private getWifiBreakdown(
    group: QuestionGroup,
  ): { wound: number; ischemia: number; footInfection: number } | null {
    const wagnerScore = this.findScoreByCode('maggitwagner')?.totalScore;
    const iwgdfScore = this.findScoreByCode('iwgdf')?.totalScore;
    const selectedOption = group.questions
      .flatMap((question) =>
        question.options.filter((option) => option.id === question.answerOptionId),
      )
      [0];

    if (
      wagnerScore === undefined ||
      iwgdfScore === undefined ||
      !selectedOption
    ) {
      return null;
    }

    return {
      wound: this.mapWagnerToWifiWoundScore(wagnerScore),
      ischemia: Math.max(0, Math.min(3, selectedOption.score)),
      footInfection: Math.max(0, Math.min(3, iwgdfScore)),
    };
  }

  getWifiBreakdownForGroup(
    group: QuestionGroup,
  ): { wound: number; ischemia: number; footInfection: number } | null {
    return this.getWifiBreakdown(group);
  }

  private mapWagnerToWifiWoundScore(wagnerScore: number): number {
    if (wagnerScore <= 0) return 0;
    if (wagnerScore === 1) return 1;
    if (wagnerScore <= 4) return 2;
    return 3;
  }

  private getWifiStage(
    woundScore: number,
    ischemiaScore: number,
    footInfectionScore: number,
  ): number {
    const matrix = [
      [
        [1, 1, 2, 3],
        [1, 1, 2, 3],
        [2, 2, 3, 4],
        [3, 3, 4, 4],
      ],
      [
        [1, 2, 3, 4],
        [1, 2, 3, 4],
        [3, 3, 4, 4],
        [4, 4, 4, 4],
      ],
      [
        [2, 2, 3, 4],
        [2, 3, 4, 4],
        [3, 4, 4, 4],
        [4, 4, 4, 4],
      ],
      [
        [2, 3, 3, 4],
        [3, 3, 4, 4],
        [4, 4, 4, 4],
        [4, 4, 4, 4],
      ],
    ];

    return matrix[ischemiaScore][woundScore][footInfectionScore];
  }

  getWifiStageText(score: number | undefined): string {
    if (!score) {
      return '';
    }

    return this.translate.instant(`patientCreate.woundAssessment.wifi.stages.${score}`);
  }

  getWifiRiskLevelText(score: number | undefined): string {
    if (!score) {
      return '';
    }

    return this.translate.instant(this.getWifiRiskLevelTranslationKey(score));
  }

  getWifiMajorAmputationRate(score: number | undefined): string {
    if (!score) {
      return '';
    }

    const rates: Record<number, string> = {
      1: '0.75%',
      2: '5.90%',
      3: '8.40%',
      4: '25%',
    };

    return rates[score] || rates[4];
  }

  private getWifiRiskLevelTranslationKey(score: number): string {
    const keys: Record<number, string> = {
      1: 'patientCreate.woundAssessment.wifi.riskLevels.veryLow',
      2: 'patientCreate.woundAssessment.wifi.riskLevels.low',
      3: 'patientCreate.woundAssessment.wifi.riskLevels.moderate',
      4: 'patientCreate.woundAssessment.wifi.riskLevels.high',
    };

    return keys[score] || keys[4];
  }

  private updateGlassGroupAvailability(): void {
    const glassGroup = this.findGroupByCode('glass');
    if (!glassGroup) {
      return;
    }

    if (!this.isDiabeticUlcerSelected()) {
      glassGroup.questions.forEach((question) => {
        question.isDisabled = false;
        question.disabledReason = undefined;
      });
      return;
    }

    const hasWifiResult = !!this.findScoreByCode('wifi');

    glassGroup.questions.forEach((question) => {
      question.isDisabled = !hasWifiResult;
      question.disabledReason = hasWifiResult
        ? undefined
        : this.translate.instant(
            'patientCreate.woundAssessment.glass.validation.dependencies',
          );

      if (!hasWifiResult) {
        question.answerOptionId = undefined;
      }
    });

    if (!hasWifiResult) {
      const templateId = glassGroup.questions[0]?.templateId;
      if (templateId) {
        this.groupScores.delete(templateId);
      }
    }
  }

  private calculateGlassGroupScore(group: QuestionGroup): void {
    const templateId = group.questions[0]?.templateId;
    if (!templateId) {
      return;
    }

    const breakdown = this.getGlassBreakdown(group);
    const wifiStage = this.findScoreByCode('wifi')?.totalScore;
    const glassStage = getGlassStage(breakdown?.fp, breakdown?.ip);

    if (!breakdown || glassStage === null || !wifiStage) {
      this.groupScores.delete(templateId);
      return;
    }

    const recommendationKey = this.getGlassRecommendationKey(glassStage, wifiStage);

    this.groupScores.set(templateId, {
      assessmentTemplateId: templateId,
      assessmentTemplateCode: group.code || 'Glass',
      totalScore: glassStage,
      riskCategoryName: this.translate.instant(
        this.getGlassStageTranslationKey(glassStage),
      ),
      description: recommendationKey
        ? this.translate.instant(
            this.getGlassRecommendationTranslationKey(recommendationKey),
          )
        : '',
      metadata: JSON.stringify(
        this.buildGlassAssessmentMetadata(
          group,
          group.questions
            .filter((question) => question.answerOptionId)
            .map((question) => ({
              assessmentQuestionId: question.id,
              assessmentAnswerOptionId: question.answerOptionId!,
            })),
          glassStage,
          recommendationKey,
        ),
      ),
    });
  }

  private recalculateGlassGroupScoreIfPossible(): void {
    const glassGroup = this.findGroupByCode('glass');
    if (!glassGroup) {
      return;
    }

    const hasAnswer = glassGroup.questions.some((question) => question.answerOptionId);
    if (!hasAnswer) {
      const templateId = glassGroup.questions[0]?.templateId;
      if (templateId) {
        this.groupScores.delete(templateId);
      }
      return;
    }

    this.calculateGlassGroupScore(glassGroup);
  }

  private getGlassBreakdown(
    group: QuestionGroup,
  ): { fp: number | null; ip: number | null; ai: number | null; pedal: number | null } | null {
    const getSelectedScore = (shortCode: string): number | null => {
      const question = group.questions.find((item) => item.shortCode === shortCode);
      const option = question?.options.find((item) => item.id === question.answerOptionId);

      return option?.score ?? null;
    };

    const breakdown = {
      fp: getSelectedScore('GLASS_FP'),
      ip: getSelectedScore('GLASS_IP'),
      ai: getSelectedScore('GLASS_AI'),
      pedal: getSelectedScore('GLASS_PEDAL'),
    };

    return Object.values(breakdown).some((value) => value !== null)
      ? breakdown
      : null;
  }

  getGlassBreakdownForGroup(
    group: QuestionGroup,
  ): { fp: number | null; ip: number | null; ai: number | null; pedal: number | null } | null {
    return this.getGlassBreakdown(group);
  }

  getGlassStageText(score: number | undefined): string {
    if (!score) {
      return '';
    }

    return this.translate.instant(this.getGlassStageTranslationKey(score));
  }

  getGlassBadgeText(score: number | undefined): string {
    return score ? `GLASS ${score}` : 'GLASS';
  }

  getGlassTreatmentRecommendationText(
    score: number | undefined,
    wifiStage: number | undefined,
  ): string {
    if (!score || !wifiStage) {
      return '';
    }

    const recommendationKey = this.getGlassRecommendationKey(score, wifiStage);
    if (!recommendationKey) {
      return '';
    }

    return this.translate.instant(
      this.getGlassRecommendationTranslationKey(recommendationKey),
    );
  }

  getWifiScoreForGlass(): number | null {
    return this.findScoreByCode('wifi')?.totalScore ?? null;
  }

  private getGlassStageTranslationKey(score: number): string {
    return `patientCreate.woundAssessment.glass.stages.${score}`;
  }

  private getGlassRecommendationKey(
    glassStage: number,
    wifiStage: number,
  ): GlassTreatmentRecommendationKey | null {
    return getGlassTreatmentRecommendation(glassStage, wifiStage);
  }

  private getGlassRecommendationTranslationKey(
    recommendationKey: GlassTreatmentRecommendationKey,
  ): string {
    return `patientCreate.woundAssessment.glass.recommendations.${recommendationKey}`;
  }

  private buildGlassAssessmentMetadata(
    group: QuestionGroup,
    answers: Array<{
      assessmentQuestionId: string;
      assessmentAnswerOptionId: string;
    }>,
    glassStage?: number | null,
    recommendationKey?: GlassTreatmentRecommendationKey | null,
  ): GlassAssessmentMetadata | null {
    const templateId = group.questions[0]?.templateId;
    const selectedVessels = templateId ? this.glassSelections.get(templateId) ?? [] : [];
    const selectedVesselsBySegment: Partial<Record<'FP' | 'IP', string[]>> = {};
    for (const vessel of selectedVessels) {
      const bucket = selectedVesselsBySegment[vessel.segment] ?? [];
      bucket.push(vessel.label);
      selectedVesselsBySegment[vessel.segment] = bucket;
    }

    const stageAnswers: Partial<Record<'FP' | 'IP', string>> = {};
    for (const answer of answers) {
      const question = group.questions.find((item) => item.id === answer.assessmentQuestionId);
      const option = question?.options.find(
        (item) => item.id === answer.assessmentAnswerOptionId,
      );
      if (!question || !option) {
        continue;
      }

      if (question.shortCode === 'GLASS_FP') {
        stageAnswers['FP'] = this.getDisplayOptionText(option.text);
      }

      if (question.shortCode === 'GLASS_IP') {
        stageAnswers['IP'] = this.getDisplayOptionText(option.text);
      }
    }

    if (
      !selectedVessels.length &&
      !stageAnswers['FP'] &&
      !stageAnswers['IP'] &&
      glassStage == null &&
      !recommendationKey
    ) {
      return null;
    }

    return {
      selectedVessels,
      selectedVesselsBySegment,
      stageAnswers,
      glassStage: glassStage ?? null,
      recommendationKey: recommendationKey ?? null,
    };
  }

  private formatWifiDisplayText(
    text: string | undefined,
    removeIschemiaPrefix = false,
  ): string {
    if (!text) {
      return '';
    }

    let formatted = text
      .replace(/\bWIFI\b/g, 'WIfI')
      .replace(/\bWifi\b/g, 'WIfI')
      .replace(/^WIfI\s*\((Wound|Yara)\)\s*(Template|Skalası)$/i, 'WIfI')
      .replace(/^WifiWound Template$/i, 'WIfI')
      .replace(/^WIfI Ischemia Classification\?$/i, 'WIfI Ischemia Classification');

    if (removeIschemiaPrefix) {
      formatted = formatted.replace(/^I[0-3]\s*-\s*/i, '');
      formatted = formatted.split('|')[0].trim();
    }

    return formatted;
  }

  onWeightInput(): void {
    const prevWeight = this.patientForm.get(
      'woundDetails.previousWeight',
    )?.value;
    const currWeight = this.patientForm.get(
      'woundDetails.currentWeight',
    )?.value;

    if (prevWeight != null && currWeight != null && prevWeight > 0) {
      const percentageLost = ((prevWeight - currWeight) / prevWeight) * 100;

      const weightLossQuestion = this.woundQuestions.find(
        (q) => q.id === this.WEIGHT_LOSS_QUESTION_ID,
      );

      if (weightLossQuestion) {
        let targetValue = '';
        if (percentageLost > 10) {
          targetValue = 'GT_10';
        } else if (percentageLost >= 5 && percentageLost <= 10) {
          targetValue = 'BETWEEN_5_10';
        } else {
          // Less than 5 (includes negative for weight gain)
          targetValue = 'LT_5';
        }

        const optionToSelect = weightLossQuestion.options.find(
          (o) => o.value === targetValue,
        );

        if (optionToSelect) {
          if (weightLossQuestion.answerOptionId !== optionToSelect.id) {
            weightLossQuestion.answerOptionId = optionToSelect.id;
            this.evaluateQuestionDependencies();
          }
          this.checkAndAdvanceAccordion(weightLossQuestion);
        }
      }
    } else {
      const weightLossQuestion = this.woundQuestions.find(
        (q) => q.id === this.WEIGHT_LOSS_QUESTION_ID,
      );
      if (weightLossQuestion && weightLossQuestion.answerOptionId) {
        weightLossQuestion.answerOptionId = undefined;
        this.evaluateQuestionDependencies();
      }
    }
  }

  trackByQuestionId(index: number, item: QuestionVM): string {
    return item.id;
  }

  trackByQuestionGroup(index: number, group: QuestionGroup): string {
    return group.code || group.title || index.toString();
  }

  getUIType(templateCode: string): 'grid' | 'list' | 'cards' {
    const code = templateCode?.toLowerCase() || '';

    if (code.includes('stage') || code.includes('wound stage')) {
      return 'cards';
    }

    if (code.includes('ceap')) {
      return 'grid';
    }

    return 'list';
  }

  formatFontaine(stageLike: any): string {
    if (stageLike === null || stageLike === undefined) return '';

    // Mapping from internal totalScore -> Fontaine stage token
    const scoreToStage: { [score: number]: string } = {
      0: '1',
      1: '2',
      2: '2A',
      3: '2B',
      4: '3',
      5: '4',
    };

    const stageToRutherford: { [stage: string]: { cat: number; grade: number } } = {
      '1': { cat: 0, grade: 0 },
      '2': { cat: 1, grade: 1 },
      '2A': { cat: 2, grade: 1 },
      '2B': { cat: 3, grade: 1 },
      '3': { cat: 4, grade: 2 },
      '4': { cat: 5, grade: 3 },
    };

    // If the input is a number-like value (score), use it directly
    const n = Number(stageLike);
    let stageToken: string | undefined;
    if (!isNaN(n) && Number.isInteger(n)) {
      stageToken = scoreToStage[n];
    }

    // If not numeric or mapping missing, try to extract from text like 'Stage-3'
    if (!stageToken) {
      const s = String(stageLike).trim().toUpperCase();
      const normalized = s.replace(/\s+/g, '');
      const stageMatch = normalized.match(/([1-4](?:A|B)?)/i);
      stageToken = stageMatch ? stageMatch[1].toUpperCase() : undefined;
    }

    if (stageToken && stageToRutherford[stageToken]) {
      const r = stageToRutherford[stageToken];
      return `Fontaine Stage ${stageToken} - Rutherford Category ${r.cat} Grade ${r.grade}`;
    }

    return String(stageLike);
  }

  formatIWGDFRiskLevel(score: any): string {
    if (score === null || score === undefined) return '';

    const numScore = Number(score);
    if (isNaN(numScore)) return '';

    let translationKey = '';

    if (numScore <= 0) {
      translationKey = 'patientCreate.woundAssessment.iwgdfRiskLevels.no';
    } else if (numScore === 1) {
      translationKey =
        'patientCreate.woundAssessment.iwgdfRiskLevels.slightly';
    } else if (numScore === 2) {
      translationKey =
        'patientCreate.woundAssessment.iwgdfRiskLevels.moderate';
    } else {
      translationKey = 'patientCreate.woundAssessment.iwgdfRiskLevels.severe';
    }

    return this.translate.instant(translationKey);
  }

  private getIWGDFFollowUpTranslationKey(score: number): string {
    const followUpKeys: { [score: number]: string } = {
      0: 'patientCreate.woundAssessment.iwgdfFollowUp.score0',
      1: 'patientCreate.woundAssessment.iwgdfFollowUp.score1',
      2: 'patientCreate.woundAssessment.iwgdfFollowUp.score2',
      3: 'patientCreate.woundAssessment.iwgdfFollowUp.score3',
    };

    return followUpKeys[score] || followUpKeys[3];
  }

  


  ngAfterViewInit(): void {
    setTimeout(() => {
      const currentValue =
        this.patientForm.get('woundDetails.painLevel')?.value || 0;
      this.updatePainSliderBackground(currentValue);
    }, 100);
  }

  updatePainSliderBackground(value: number): void {
    const percentage = (value / 10) * 100;
    const slider = document.querySelector(
      '.pain-level-slider .form-range',
    ) as HTMLInputElement;
    if (slider) {
      slider.style.background = `linear-gradient(to right, #ff6b6b 0%, #ff6b6b ${percentage}%, #555 ${percentage}%, #555 100%)`;
    }
  }

  onSymptomChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const value = checkbox.value;
    const currentSymptoms =
      this.patientForm.get('woundDetails.symptoms')?.value || [];

    let updatedSymptoms: string[];
    if (checkbox.checked) {
      if (!currentSymptoms.includes(value)) {
        updatedSymptoms = [...currentSymptoms, value];
      } else {
        updatedSymptoms = currentSymptoms;
      }
    } else {
      updatedSymptoms = currentSymptoms.filter((s: string) => s !== value);

      if (value === 'discharge') {
        updatedSymptoms = updatedSymptoms.filter(
          (s: string) => !['serous', 'purulent', 'sanguineous'].includes(s),
        );
      }
    }

    this.patientForm.get('woundDetails.symptoms')?.setValue(updatedSymptoms);
  }

  onDischargeTypeChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const value = checkbox.value;
    const currentSymptoms =
      this.patientForm.get('woundDetails.symptoms')?.value || [];

    let updatedSymptoms: string[];
    if (checkbox.checked) {
      if (!currentSymptoms.includes(value)) {
        updatedSymptoms = [...currentSymptoms, value];
      } else {
        updatedSymptoms = currentSymptoms;
      }
    } else {
      updatedSymptoms = currentSymptoms.filter((s: string) => s !== value);
    }

    this.patientForm.get('woundDetails.symptoms')?.setValue(updatedSymptoms);
  }

  dischargeValidator(control: AbstractControl): ValidationErrors | null {
    const symptoms = control.get('symptoms')?.value || [];
    const hasDischarge = symptoms.includes('discharge');

    if (hasDischarge) {
      const dischargeTypes = ['serous', 'purulent', 'sanguineous'];
      const hasDischargeType = symptoms.some((symptom: string) =>
        dischargeTypes.includes(symptom),
      );

      if (!hasDischargeType) {
        return { dischargeTypeRequired: true };
      }
    }

    return null;
  }

  calculateAssessmentScores(): void {
    console.log(this.selectedAntibiotics);
    console.log(this.selectedWoundCareProducts);
    console.log(this.selectedDebridements);
    console.log(this.selectedVacSubOptions);
    const hasWound = this.patientForm.get('woundDetails.hasWound')?.value;

    const groups = new Map<string, CalculateAssessmentsScoresRequest>();

    this.questionGroups.forEach((group) => {
      group.questions
        .filter((q) => q.answerOptionId)
        .forEach((q) => {
          if (!q.templateId) return;

          if (!groups.has(q.templateId)) {
            groups.set(q.templateId, {
              assessmentTemplateId: q.templateId,
              answers: [],
            });
          }

          groups.get(q.templateId)?.answers.push({
            assessmentQuestionId: q.id,
            assessmentAnswerOptionId: q.answerOptionId,
          });
        });

      if (this.isGlassGroup(group)) {
        const templateId = group.questions[0]?.templateId;
        const request = templateId ? groups.get(templateId) : undefined;
        const metadata = request
          ? this.buildGlassAssessmentMetadata(group, request.answers)
          : null;

        if (request && metadata) {
          request.metadata = JSON.stringify(metadata);
        }
      }
    });

    const calculateAssessmentsScores = Array.from(groups.values());

    if (!calculateAssessmentsScores.length && !hasWound) {
      this.toastService.showToast(
        'Please answer at least one assessment question or provide wound details.',
        'error',
      );
      return;
    }

    const woundDetails = this.patientForm.get('woundDetails')?.value;
    const createWoundDto = {
      recordId: this.savedPatientRecordId,
      woundType:
        this.woundTypeParam !== null
          ? this.woundTypeParam
          : hasWound === true
            ? 1
            : 0,
      parentWoundId: this.parentWoundId || null,
      woundCategoryType: this.parentWoundId ? 1 : 0,
      name: hasWound ? 'Wound Assessment' : undefined,
      description: hasWound ? 'Wound Assessment Data' : undefined,
      ulcerWoundTypeIds: hasWound
        ? Array.isArray(woundDetails?.ulcerWoundType)
          ? woundDetails.ulcerWoundType
          : woundDetails?.ulcerWoundType
            ? [woundDetails.ulcerWoundType]
            : []
        : [],
      symptomIds: hasWound
        ? Array.isArray(woundDetails?.symptoms)
          ? woundDetails.symptoms
          : woundDetails?.symptoms
            ? [woundDetails.symptoms]
            : []
        : [],
      dischargeType: hasWound ? (woundDetails?.dischargeType ?? 0) : 0,
      onset: hasWound ? woundDetails?.onset : undefined,
      healingStatus: hasWound ? woundDetails?.healingStatus : 0,
      painLevel: hasWound ? woundDetails?.painLevel : 0,
      priorManagement: hasWound
        ? Array.isArray(woundDetails?.priorManagement)
          ? woundDetails.priorManagement
          : woundDetails?.priorManagement
            ? [woundDetails.priorManagement]
            : []
        : [],
      weight: !hasWound ? woundDetails?.currentWeight : null,
    };

    const body = {
      calculateAssessmentsScores,
      createWoundDto,
      woundConsultationId:
        this.woundConsultationId !== '' ? this.woundConsultationId : null,
      antibioticIds: this.selectedAntibiotics ?? null,
      debridementIds: this.selectedDebridements ?? null,
      woundCareProductIds: this.selectedWoundCareProducts ?? null,
      createVacuumAssistedClosure: this.buildVacDto(),
    };

    this.woundService.createWound(body).subscribe({
      next: (res) => {
        if (res.isSuccess && res.data) {
          this.toastService.showToast(
            this.translate.instant('patientCreate.successWound'),
            'success',
          );
          this.savedWoundId = res.data.id;
          this.markConsultationActionCompletedLocally();
          this.router.navigateByUrl(`patient/detail/${this.patientId}`);
        } else {
          console.error('Failed to calculate assessment scores:', res.fail);
          this.toastService.showToast(
            this.translate.instant('patientCreate.errorWound'),
            'error',
          );
        }
      },
      error: (err) => {
        console.error('Error calculating assessment scores:', err);
      },
    });
  }

  private markConsultationActionCompletedLocally(): void {
    if (!this.woundConsultationId) {
      return;
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(
          `${this.consultationActionCompletedStoragePrefix}${this.woundConsultationId}`,
          Date.now().toString(),
        );
      }
    } catch {
      // localStorage can be unavailable in restricted browser contexts.
    }
  }

  toggleAccordion(section: string) {
    // Medical history sections - only one should be open at a time
    const medicalHistorySections = [
      'woundCareProducts',
      'antibiotics',
      'debridement',
      'vac',
    ];

    if (medicalHistorySections.includes(section)) {
      // Close all other medical history sections
      medicalHistorySections.forEach((s) => {
        if (s !== section) {
          this.accordionState[s] = false;
        }
      });
      // Toggle the current section
      this.accordionState[section] = !this.accordionState[section];
    } else {
      // For non-medical history sections, just toggle
      this.accordionState[section] = !this.accordionState[section];
    }
  }

  toggleNestedAccordion(items: any[], index: number) {
    // Close all other items in this nested group
    items.forEach((item, i) => {
      if (i !== index) {
        item.isOpen = false;
      }
    });
    // Toggle the current item
    items[index].isOpen = !items[index].isOpen;
  }

  async getListFamilyMedicalHistory() {
    var res =
      await this.familyMedialHistoryService.getListFamilyMedicalHistory();

    this.familyMedicalHistory = res.data;
  }

  async getListImmunizationStatus() {
    var res = await this.immunizationService.getListImmunizationStatus();

    this.immunizationStatus = res.data;
  }

  async getListUlcerWoundType() {
    const res = await this.ulcerWoundTypeService.getListUlcerWoundType();
    this.ulcerWoundTypeOptions = res.data;
  }

  async getListSymptom() {
    const res = await this.symptomService.getListSymptom();
    this.symptomOptions = res.data;
  }

  toggleFamilyHistory(id: string): void {
    console.log('geldi:', id);
    const index = this.selectedFamilyHistory.indexOf(id);
    if (index > -1) {
      this.selectedFamilyHistory.splice(index, 1);
    } else {
      this.selectedFamilyHistory.push(id);
    }
  }

  isFamilyHistorySelected(id: string): boolean {
    return this.selectedFamilyHistory.includes(id);
  }

  toggleImmunization(id: string): void {
    const index = this.selectedImmunizations.indexOf(id);
    if (index > -1) {
      this.selectedImmunizations.splice(index, 1);
    } else {
      this.selectedImmunizations.push(id);
    }
  }

  isImmunizationSelected(id: string): boolean {
    return this.selectedImmunizations.includes(id);
  }

  private setupSearchTermListener() {
    this.chronicConditionService.setupSearchTermListener(
      this.patientForm.get('medicalHistory') as FormGroup,
      'searchTerm',
      (res: any) => {
        this.results = res.data.items;
        this.totalCount = res.data.count;
        this.hasNext = res.data.hasNext;
        this.currentPageIndex = 0;
        this.showDropdown = res.data.items.length > 0;
      },
      () => {},
    );
  }

  selectItem(item: ChronicCondition) {
    this.chronicConditionService.selectItem(
      this.patientForm.get('medicalHistory') as FormGroup,
      'searchTerm',
      item,
      this.chronicConditionArray,
      () => {
        this.results = [];
        this.showDropdown = false;
        this.currentPageIndex = 0;
      },
    );
  }

  onChildSearchTermChange(term: string) {
    const control = this.patientForm.get('medicalHistory.searchTerm');
    if (control) {
      control.setValue(term, { emitEvent: true });
    }
  }

  removeCondition(index: number) {
    this.chronicConditionService.removeCondition(
      index,
      this.chronicConditionArray,
    );
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) return;
    let sanitized = input.value.replace(/[^0-9-]/g, '');
    const parts = sanitized.split('-');

    if (parts.length >= 1 && parts[0].length > 4) {
      parts[0] = parts[0].substring(0, 4);
    }

    if (parts.length >= 2 && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }

    if (parts.length >= 3 && parts[2].length > 2) {
      parts[2] = parts[2].substring(0, 2);
    }

    const newValue = parts.join('-');

    if (newValue !== input.value) {
      input.value = newValue;
      this.patientForm
        .get('personalInfo.dob')
        ?.setValue(newValue, { emitEvent: false });
    }
  }

  private initSelectOptions(): void {
    this.initGenderOptions();
    this.initMaritalStatusOptions();
    this.initWoundOptions();
    this.initLifestyleOptions();
  }

  private initGenderOptions(): void {
    this.genders = [
      { id: 2, name: this.translate.instant('male') },
      { id: 3, name: this.translate.instant('female') },
      { id: 1, name: this.translate.instant('other') },
    ];
  }

  private initMaritalStatusOptions(): void {
    this.maritalStatuses = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.form.maritalStatusOptions.single',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.form.maritalStatusOptions.married',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.form.maritalStatusOptions.divorced',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.form.maritalStatusOptions.widow',
        ),
      },
    ];
  }

  private initWoundOptions(): void {
    this.healingStatusOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.worsening',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.stable',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.improving',
        ),
      },
    ];
    this.priorManagementOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.systemicAntibiotics',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.topicalAntibiotics',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.dressings',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.woundCareProducts',
        ),
      },
      {
        id: 5,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.solutions',
        ),
      },
      {
        id: 6,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.topicalGrowthFactors',
        ),
      },
      {
        id: 7,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.intralesionalGrowthFactors',
        ),
      },
      {
        id: 8,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.biophysicalAgents',
        ),
      },
      {
        id: 9,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.exosomeTherapy',
        ),
      },
      {
        id: 10,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.plateletRichPlasma',
        ),
      },
      {
        id: 11,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.stemCellTherapy',
        ),
      },
      {
        id: 12,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.debridement',
        ),
      },
      {
        id: 13,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.vacuumAssistedClosure',
        ),
      },
      {
        id: 14,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.surgery',
        ),
      },
    ];

    this.dischargeTypeOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.woundAssessment.symptomOptions.serous',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.woundAssessment.symptomOptions.purulent',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.woundAssessment.symptomOptions.sanguineous',
        ),
      },
    ];
  }

  private initLifestyleOptions(): void {
    this.dietTypeOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.dietTypes.regular',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.dietTypes.vegetarian',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.dietTypes.vegan',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.dietTypes.keto',
        ),
      },
      {
        id: 5,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.dietTypes.other',
        ),
      },
    ];

    this.skinTypeOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeI',
        ),
        color: 'rgb(243, 208, 176)',
        textColor: '#1E1E1E',
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeII',
        ),
        color: 'rgb(231, 180, 143)',
        textColor: '#1E1E1E',
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeIII',
        ),
        color: 'rgb(210, 158, 125)',
        textColor: '#1E1E1E',
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeIV',
        ),
        color: 'rgb(186, 119, 80)',
        textColor: '#FFFFFF',
      },
      {
        id: 5,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeV',
        ),
        color: 'rgb(122, 67, 30)',
        textColor: '#FFFFFF',
      },
      {
        id: 6,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.skinTypes.typeVI',
        ),
        color: 'rgb(60, 33, 29)',
        textColor: '#FFFFFF',
      },
    ];

    this.bloodTypeOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.aPositive',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.aNegative',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.bPositive',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.bNegative',
        ),
      },
      {
        id: 5,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.abPositive',
        ),
      },
      {
        id: 6,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.abNegative',
        ),
      },
      {
        id: 7,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.oPositive',
        ),
      },
      {
        id: 8,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.bloodTypes.oNegative',
        ),
      },
    ];

    this.alcoholConsumptionOptions = [
      {
        id: 0,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.alcoholConsumption.never',
        ),
      },
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.alcoholConsumption.occasionally',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.alcoholConsumption.frequently',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.alcoholConsumption.daily',
        ),
      },
    ];

    this.smokingStatusOptions = [
      {
        id: 0,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.smokingStatus.never',
        ),
      },
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.smokingStatus.former',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.smokingStatus.current',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.physicalLifestyle.smokingStatus.quitted',
        ),
      },
    ];
  }

  showTooltip(event: MouseEvent, tooltip: string) {
    if (!tooltip) return;

    // Store the initial position
    this.pendingTooltipPosition = {
      x: event.clientX + 12,
      y: event.clientY - 12,
    };

    // Clear any existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    // Set a 300ms delay before showing the tooltip
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: tooltip,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  updateTooltipPosition(event: MouseEvent) {
    const newX = event.clientX + 12;
    const newY = event.clientY - 12;

    // Update pending position if tooltip hasn't appeared yet
    if (this.pendingTooltipPosition) {
      this.pendingTooltipPosition.x = newX;
      this.pendingTooltipPosition.y = newY;
    }

    // Update active tooltip position if it's already visible
    if (this.activeTooltip) {
      this.activeTooltip.x = newX;
      this.activeTooltip.y = newY;
    }
  }

  hideTooltip() {
    // Clear the timeout if user leaves before tooltip appears
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }
}
